"use client";

import { useState } from "react";
import { LegalFooter } from "@/components/LegalFooter";
import { heroIntents } from "@/components/landing/homepage-data";
import { LandingAnnouncementBar } from "@/components/landing/LandingAnnouncementBar";
import { LandingBeforeAfter } from "@/components/landing/LandingBeforeAfter";
import { LandingConnectedPlatform } from "@/components/landing/LandingConnectedPlatform";
import { LandingCredibilityStrip } from "@/components/landing/LandingCredibilityStrip";
import { LandingFAQ } from "@/components/landing/LandingFAQ";
import { LandingHero } from "@/components/landing/LandingHero";
import { LandingShowcaseCarousel } from "@/components/landing/LandingShowcaseCarousel";
import { AppHeader } from "@/components/layout/AppHeader";
import { LandingPricing } from "@/components/landing/LandingPricing";
import { LandingStatsStrip } from "@/components/landing/LandingStatsStrip";
import { LandingToolsGrid } from "@/components/landing/LandingToolsGrid";
import { LandingUseCases } from "@/components/landing/LandingUseCases";
import { NewFeatureRow } from "@/components/home/NewFeatureRow";
import type { Announcement } from "@/lib/landing/announcement";


/**
 * @param announcement Admin-configured announcement bar, resolved server-side so
 *   it lands in the SSR HTML and never shifts the hero down after hydration.
 *   Null (the default) renders no bar at all.
 */
export default function Landing({ announcement = null }: { announcement?: Announcement | null }) {
  // Manual-only intent switching. The 8s auto-rotate timer was removed because
  // it swapped the hero iframe src to a multi-MB doc on a schedule — combined
  // with the click-to-activate hero visual, nothing heavy loads until the
  // visitor opts in. Pills still switch the hero text/label instantly.
  const [selectedIntent, setSelectedIntent] = useState(heroIntents[0]);

  return (
    <div className="lb-page min-h-screen overflow-x-clip">
      {/* Above the sticky header on purpose — it scrolls away with the page. */}
      <LandingAnnouncementBar announcement={announcement} />
      <AppHeader />
      <main>
        <LandingHero intent={selectedIntent} setIntent={setSelectedIntent} />
        <NewFeatureRow variant="landbook" />
        <LandingCredibilityStrip />
        <LandingStatsStrip />
        <LandingToolsGrid />
        <LandingShowcaseCarousel />
        <LandingConnectedPlatform />
        <LandingUseCases />
        <LandingBeforeAfter />
        <LandingPricing />
        <LandingFAQ />
      </main>
      <LegalFooter />
    </div>
  );
}
